import {
  AuthUsecaseInterface,
  SignInParameter,
  SignUpParameter,
  ConfirmSignUpParameter,
  CompleteNewPasswordParameter,
  ForgotPasswordParameter,
} from "./usecase.d";
import { AuthRepository } from "../../services/Auth/auth.repository";
import {
  ChangePasswordParameter,
  UpdateUserAttributesParameter,
  ForgotPasswordSubmitParameter,
  ResendSignUpParameter,
} from "../../services/Auth/auth.repository.d";
import { GetPresignedUrlParameter } from "../../services/Auth/dto/presignedUrl.dto";
import {
  SignedInDto,
  UserGroupType,
} from "../../services/Auth/dto/signedIn.dto";
import { AuthDispatch } from "./reducer";
import { ActionType } from "./types";

export class AuthUsecase implements AuthUsecaseInterface {
  repository: AuthRepository;
  dispatch: AuthDispatch;

  constructor(dispatch?: AuthDispatch) {
    this.repository = new AuthRepository();
    if (dispatch) {
      this.dispatch = dispatch;
    }
  }

  async signIn(params: SignInParameter) {
    const { username, password } = params;
    try {
      const cognitoUser = await this.repository.signIn({
        username,
        password,
      });

      if (cognitoUser.challengeName !== "NEW_PASSWORD_REQUIRED") {
        const homeBuilderRoles: Array<UserGroupType | ""> = [
          "SuperModerator",
          "Moderator",
          "Sale",
        ];
        if (!homeBuilderRoles.includes(cognitoUser.group || "")) {
          await this.signOut();
          throw new Error("Authentication failed");
        }

        this.dispatch({
          type: ActionType.SetUserInfo,
          userInfo: cognitoUser,
        });
      }

      return cognitoUser;
    } catch (error) {
      if (error.message === "No Permission") {
        throw new Error(
          "The provided account details do not have permission to log into Home Explorer. Please contact your administrator.",
        );
      }
      if (
        error.code === "NotAuthorizedException" &&
        error.message === "User is disabled."
      ) {
        throw new Error(
          "Your account already disabled. Please contact administrator for further information!",
        );
      }
      if (error && error.code === "NotAuthorizedException") {
        throw new Error(
          "The email, username or password that you entered is not right. Please try again.",
        );
      }
      throw error;
    }
  }

  signUp(params: SignUpParameter) {
    const { clientId, tenantId, username, password, firstName, lastName } =
      params;
    return this.repository.signUp({
      clientId,
      tenantId,
      username,
      password,
      firstName,
      lastName,
    });
  }

  confirmSignUp(params: ConfirmSignUpParameter) {
    const { clientId, tenantId, username, code } = params;
    return this.repository.confirmSignUp({
      clientId,
      tenantId,
      username,
      code,
    });
  }

  resendSignUp(params: ResendSignUpParameter) {
    const { username } = params;
    return this.repository.resendSignUp({ username });
  }

  async completeNewPassword(params: CompleteNewPasswordParameter) {
    const { cognitoUser, password } = params;
    await this.repository.completeNewPassword({
      cognitoUser,
      password,
    });

    const signedIn = await this.signIn({
      username: cognitoUser.username,
      password: password,
    });

    const isAllowGroup = await this.isAllowedGroup(signedIn);
    if (isAllowGroup) {
      return signedIn;
    } else {
      throw new Error(
        "Your user do not have permission. Please contact administrator for more information!",
      );
    }
  }

  forgotPassword(params: ForgotPasswordParameter) {
    const { email, clientId, tenantId } = params;
    return this.repository.forgotPassword({ email, clientId, tenantId });
  }

  forgotPasswordSubmit(params: ForgotPasswordSubmitParameter) {
    const { username, code, password } = params;
    return this.repository.forgotPasswordSubmit({ username, code, password });
  }

  private isAllowedGroup(cognitoUser: SignedInDto) {
    const userGroups =
      cognitoUser.signInUserSession.idToken.payload["cognito:groups"];
    const allowGroups = ["HomeBuyer", "SuperModerator", "Moderator", "Sale"];
    return userGroups?.some((groupName) => allowGroups.includes(groupName));
  }

  currentAuthenticatedUser() {
    return this.repository.currentAuthenticatedUser();
  }

  async syncUserInfo(bypassCache = false) {
    const cognitoUserInfo = await this.repository.currentAuthenticatedUser(
      bypassCache,
    );
    this.dispatch({
      type: ActionType.SetUserInfo,
      userInfo: cognitoUserInfo,
    });
  }

  async signOut() {
    await this.repository.signOut();
    this.dispatch({
      type: ActionType.Logout,
    });
  }

  async changePassword(params: ChangePasswordParameter) {
    const { user, oldPassword, newPassword } = params;
    await this.repository.changePassword({ user, oldPassword, newPassword });
    await this.signOut();
  }

  async updateUserAttributes(params: UpdateUserAttributesParameter) {
    const { user, attrs } = params;
    await this.repository.updateUserAttributes({
      user,
      attrs,
    });

    await this.syncUserInfo();
  }

  getPresignedUrl(params: GetPresignedUrlParameter) {
    return this.repository.getPresignedUrl(params);
  }

  getDownloadUrl(s3Uri: string) {
    return this.repository.getDownloadUrl(s3Uri);
  }
}
